import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("loading");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("unknown"); return; }
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/status/${sessionId}`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          setDetail(d);
          setStatus(d.payment_status === "paid" ? "paid" : "pending");
        } else {
          setStatus("unknown");
        }
      } catch {
        setStatus("unknown");
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0F1629] flex items-center justify-center px-4 dark-section" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader size={40} className="text-brand-green animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Confirming your payment…</h1>
            <p className="text-gray-400 text-sm">This only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-brand-green/10 border border-brand-green/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={36} className="text-brand-green" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-heading font-black text-white mb-3">Payment Confirmed!</h1>
            <p className="text-gray-400 mb-2">
              {detail?.lookup_key?.includes("plus") || detail?.lookup_key?.includes("monthly")
                ? "Welcome to ActivitySports Plus! Your benefits are now active."
                : "Your spot is confirmed. Get ready to play!"}
            </p>
            <p className="text-xs text-gray-600 mb-8">Reference: {sessionId?.slice(0, 12)}…</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/sessions"
                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors"
                data-testid="view-sessions-btn"
              >
                View My Sessions <ArrowRight size={16} />
              </Link>
              <Link
                to="/find-partners"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-white font-medium rounded-xl hover:border-white/25 transition-colors"
              >
                Find Partners
              </Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader size={40} className="text-yellow-400 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing payment…</h1>
            <p className="text-gray-400 text-sm">Waiting for payment confirmation from Stripe.</p>
          </>
        )}
        {status === "unknown" && (
          <>
            <div className="w-20 h-20 rounded-full bg-brand-green/10 border border-brand-green/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={36} className="text-brand-green" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-heading font-black text-white mb-3">Thank You!</h1>
            <p className="text-gray-400 mb-8">Your payment is being processed. Check your email for confirmation.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-green text-brand-navy font-bold rounded-xl hover:bg-brand-green/90 transition-colors">
              Back to Home <ArrowRight size={16} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
